/**
 * QuickUploadModal - Simplified 2-step wizard for uploading a new snapshot to an existing dataset.
 *
 * Full Create Dataset wizard has 4 steps (Upload -> AI Config -> Review Config -> Execute).
 * For a repeat upload to an already-created dataset we only need:
 *   Step 1: Upload the new file (same schema expected; backend auto-reuses existing config)
 *   Step 1.5: Schema diff check (auto-runs after upload, prompts on additive/breaking changes)
 *   Step 2: Execute (mode=append), reuses ExecuteStep with mode prop
 *
 * Flow:
 *   1. User picks a file -> upload to S3 via /upload/url -> /upload/confirm -> /parse/file
 *   1.5 Client calls POST /api/datasets/:id/schema-diff with parsed schema
 *       - identical: proceed silently
 *       - additive: prompt user to evolve schema
 *       - breaking: block upload, suggest creating new dataset
 *   2. Client calls POST /api/datasets/:id/batches to register a batch (returns batchId)
 *   3. Client invokes ExecuteStep with mode=append + batchId
 *   4. ExecuteStep calls /transform/execute with mode=append
 *   5. Glue Job writes new snapshot_date partition, does NOT re-create Glue table
 *   6. ExecuteStep PATCH /datasets/:id/batch-complete to bump counters
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '@amzn/awsui-components-console/modal';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Button from '@amzn/awsui-components-console/button';
import Alert from '@amzn/awsui-components-console/alert';
import Spinner from '@amzn/awsui-components-console/spinner';
import Wizard from '@amzn/awsui-components-console/wizard';
import Table from '@amzn/awsui-components-console/table';
import { UploadSampleStep } from '../wizard/steps/UploadSampleStep';
import { ExecuteStep } from '../wizard/steps/ExecuteStep';
import type { Dataset, DatasetConfig } from '../../../types/starnexus';
import { apiUrl, apiFetch, apiFetchDefaults } from '../../../utils/apiClient';

interface SchemaDiffResult {
  compatibility: 'identical' | 'additive' | 'breaking';
  added?: Array<{ columnName: string; dataType: string }>;
  removed?: Array<{ columnName: string; dataType: string }>;
  typeChanges?: Array<{ columnName: string; oldType: string; newType: string }>;
}

interface EvolveWarning {
  step: 'glue' | 'andes';
  message: string;
  fallback?: { sql: string; dataCentralLink: string };
}

interface EvolveResult {
  ok: boolean;
  newSchemaVersion: number;
  glue: 'updated' | 'skipped' | 'failed';
  andes: 'updated' | 'skipped' | 'failed';
  warnings: EvolveWarning[];
}

interface QuickUploadModalProps {
  visible: boolean;
  dataset: Dataset;
  onDismiss: () => void;
  onBatchStarted?: (batchId: string) => void;
}

/** Build a DatasetConfig-like shape from a persisted Dataset, so ExecuteStep can reuse. */
function buildConfigFromDataset(dataset: Dataset): DatasetConfig {
  const anyDs = dataset as any;
  return dataset.config ?? {
    basicSettings: {
      datasetName: dataset.datasetName,
      teamId: dataset.teamId,
      description: dataset.description || '',
      processingType: dataset.processingType || 'csv-to-json',
    },
    schemaConfig: anyDs.schema || [],
    processingOptions: {
      concurrencyLimit: 10,
      retryPolicy: { maxAttempts: 3, backoffMultiplier: 2 },
      validation: { checkSchema: true, checkSize: true, checkDuplicates: true },
    },
    andesSettings: {
      provider: dataset.providerName,
      tableName: dataset.tableName,
      tableVersion: dataset.schemaVersion || 1,
      partitionKeys: anyDs.partitionKeys || [],
    },
    outputConfig: anyDs.outputConfig || {
      track: dataset.track || 'dual',
      loadMode: 'APPEND',
      partitionType: 'TIME',
      retentionDays: 90,
    },
  };
}

/** Infer SchemaColumn array from preview headers (row 0). */
function inferSchemaFromPreview(preview: string[][]): Array<{ columnName: string; dataType: string }> {
  if (!preview || preview.length === 0) return [];
  return preview[0].map(h => ({ columnName: h, dataType: 'string' }));
}

export const QuickUploadModal: React.FC<QuickUploadModalProps> = ({ visible, dataset, onDismiss, onBatchStarted }) => {
  const navigate = useNavigate();
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string[][] | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileId, setFileId] = useState<string | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [registering, setRegistering] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [execCompleted, setExecCompleted] = useState(false);

  // Schema diff state
  const [schemaCheck, setSchemaCheck] = useState<'pending' | 'identical' | 'additive' | 'breaking' | null>(null);
  const [schemaDiff, setSchemaDiff] = useState<SchemaDiffResult | null>(null);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [evolving, setEvolving] = useState(false);
  const [evolvedVersion, setEvolvedVersion] = useState<number | null>(null);
  const [evolveResult, setEvolveResult] = useState<EvolveResult | null>(null);

  const reset = useCallback(() => {
    setActiveStepIndex(0);
    setUploadedFile(null);
    setFilePreview(null);
    setFileError(null);
    setFileId(null);
    setBatchId(null);
    setRegisterError(null);
    setExecCompleted(false);
    setSchemaCheck(null);
    setSchemaDiff(null);
    setSchemaError(null);
    setEvolving(false);
    setEvolvedVersion(null);
    setEvolveResult(null);
  }, []);

  const handleCancel = () => { reset(); onDismiss(); };

  /** Run schema diff check after file upload completes. */
  const checkSchemaDiff = useCallback(async (preview: string[][]) => {
    setSchemaCheck('pending');
    setSchemaError(null);
    setSchemaDiff(null);
    try {
      const schema = inferSchemaFromPreview(preview);
      const res = await apiFetch(apiUrl(`/api/datasets/${encodeURIComponent(dataset.datasetId)}/schema-diff`), {
      ...apiFetchDefaults,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schema }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const diff: SchemaDiffResult = await res.json();
      setSchemaDiff(diff);
      setSchemaCheck(diff.compatibility);
    } catch (e: any) {
      setSchemaError(`Schema check failed: ${e.message}`);
      setSchemaCheck(null);
    }
  }, [dataset.datasetId]);

  const handleFileChange = useCallback(
    async (file: File | null, preview: string[][] | null, error: string | null) => {
      setUploadedFile(file);
      setFilePreview(preview);
      setFileError(error);
      setFileId(null);
      setBatchId(null);
      setSchemaCheck(null);
      setSchemaDiff(null);
      setSchemaError(null);
      setEvolvedVersion(null);
      if (!file || error) return;

      try {
        const urlRes = await apiFetch(apiUrl('/api/upload/url'), {
      ...apiFetchDefaults,
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            datasetId: dataset.datasetId,
            fileName: file.name,
            contentType: file.type,
            fileSize: file.size,
          }),
        });
        if (!urlRes.ok) throw new Error('Failed to get upload URL');
        const { uploadUrl, fileId: fId } = await urlRes.json();

        await fetch(uploadUrl, {
          method: 'PUT', body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });

        await apiFetch(apiUrl('/api/upload/confirm'), {
      ...apiFetchDefaults,
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ datasetId: dataset.datasetId, fileId: fId }),
        });

        try {
          await apiFetch(apiUrl('/api/parse/file'), {
      ...apiFetchDefaults,
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ datasetId: dataset.datasetId, fileId: fId }),
          });
        } catch { /* non-critical */ }

        setFileId(fId);

        // Trigger schema diff check after successful upload
        if (preview && preview.length > 0) {
          checkSchemaDiff(preview);
        }
      } catch (e: any) {
        setFileError(`Upload failed: ${e.message}`);
      }
    },
    [dataset.datasetId, checkSchemaDiff],
  );

  /** Evolve schema (additive case), then show result panel. */
  const handleEvolveSchema = async () => {
    if (!filePreview || !schemaDiff?.added) return;
    setEvolving(true);
    setEvolveResult(null);
    try {
      const fullSchema = inferSchemaFromPreview(filePreview);
      const res = await apiFetch(apiUrl(`/api/datasets/${encodeURIComponent(dataset.datasetId)}/evolve-schema`), {
      ...apiFetchDefaults,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schema: fullSchema, newColumns: schemaDiff.added }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || `HTTP ${res.status}`); }
      const data: EvolveResult = await res.json();
      setEvolveResult(data);
      setEvolvedVersion(data.newSchemaVersion ?? null);

      // All good: auto-proceed after 1.5s
      const andesOk = data.andes === 'updated' || data.andes === 'skipped';
      if (data.glue === 'updated' && andesOk) {
        setSchemaCheck('identical');
        setTimeout(() => { handleGoToExecute(); }, 1500);
      }
      // Glue ok but andes failed: don't block, let user choose
      // Glue failed: manual intervention needed, stay on page
    } catch (e: any) {
      setSchemaError(`Schema evolution failed: ${e.message}`);
    } finally {
      setEvolving(false);
    }
  };

  /** Continue upload after andes-only failure (data still reaches S3 + Glue). */
  const handleContinueDespiteAndes = async () => {
    setSchemaCheck('identical');
    setEvolveResult(null);
    await handleGoToExecute();
  };

  const handleGoToExecute = async () => {
    if (!fileId) return;
    setRegistering(true);
    setRegisterError(null);
    try {
      const r = await apiFetch(apiUrl(`/api/datasets/${encodeURIComponent(dataset.datasetId)}/batches`), {
      ...apiFetchDefaults,
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileId }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || `HTTP ${r.status}`); }
      const data = await r.json();
      setBatchId(data.batchId);
      if (onBatchStarted) onBatchStarted(data.batchId);
      setActiveStepIndex(1);
    } catch (e: any) {
      setRegisterError(e.message);
    } finally {
      setRegistering(false);
    }
  };

  const config = buildConfigFromDataset(dataset);

  // Schema diff UI rendered between upload and execute button
  const schemaDiffUI = (() => {
    if (schemaCheck === 'pending') {
      return <Alert type="info"><Spinner /> Checking schema compatibility...</Alert>;
    }
    if (schemaError) {
      return <Alert type="warning" dismissible onDismiss={() => setSchemaError(null)}>{schemaError}</Alert>;
    }
    if (schemaCheck === 'additive' && schemaDiff?.added) {
      // Show evolve result panel after response arrives
      if (evolveResult) {
        const andesOk = evolveResult.andes === 'updated' || evolveResult.andes === 'skipped';
        const glueOk = evolveResult.glue === 'updated';
        const andesWarnings = evolveResult.warnings.filter(w => w.step === 'andes');
        const glueWarnings = evolveResult.warnings.filter(w => w.step === 'glue');

        if (glueOk && andesOk) {
          return (
            <Alert type="success">
              Schema evolved to v{evolveResult.newSchemaVersion}. All consumers will see new columns.
            </Alert>
          );
        }

        // Glue failed: manual intervention needed
        if (!glueOk) {
          return (
            <SpaceBetween size="s">
              <Alert type={andesOk ? 'warning' : 'error'} header="Schema evolution partially failed">
                {glueWarnings.map((w, i) => <Box key={i}><b>Glue:</b> {w.message}</Box>)}
                {!andesOk && andesWarnings.map((w, i) => <Box key={i}><b>Andes:</b> {w.message}</Box>)}
              </Alert>
              {andesWarnings.filter(w => w.fallback).map((w, i) => (
                <Box key={i} padding="s" variant="code">
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{w.fallback!.sql}</pre>
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button iconName="copy" variant="inline-link"
                      onClick={() => navigator.clipboard.writeText(w.fallback!.sql)}>Copy SQL</Button>
                    <Button iconName="external" variant="inline-link"
                      href={w.fallback!.dataCentralLink} target="_blank">Open in DataCentral</Button>
                  </SpaceBetween>
                </Box>
              ))}
              <Box textAlign="right">
                <Button variant="link" onClick={handleCancel}>Cancel upload</Button>
              </Box>
            </SpaceBetween>
          );
        }

        // Glue ok, andes failed: don't block upload
        return (
          <SpaceBetween size="s">
            <Alert type="warning" header="Andes table not auto-updated">
              Schema evolved to v{evolveResult.newSchemaVersion} in DDB and Glue. Andes update failed but upload can proceed.
            </Alert>
            {andesWarnings.filter(w => w.fallback).map((w, i) => (
              <Box key={i} padding="s" variant="code">
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{w.fallback!.sql}</pre>
                <SpaceBetween direction="horizontal" size="xs">
                  <Button iconName="copy" variant="inline-link"
                    onClick={() => navigator.clipboard.writeText(w.fallback!.sql)}>Copy SQL</Button>
                  <Button iconName="external" variant="inline-link"
                    href={w.fallback!.dataCentralLink} target="_blank">Open in DataCentral</Button>
                </SpaceBetween>
              </Box>
            ))}
            {andesWarnings.filter(w => !w.fallback).map((w, i) => (
              <Box key={i} color="text-status-warning">{w.message}</Box>
            ))}
            <Box textAlign="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={handleCancel}>Cancel</Button>
                <Button variant="primary" onClick={handleContinueDespiteAndes}>Continue upload anyway</Button>
              </SpaceBetween>
            </Box>
          </SpaceBetween>
        );
      }

      // Pre-evolve: show diff table + evolve button
      if (evolving) {
        return <Alert type="info"><Spinner /> Updating schema...</Alert>;
      }
      return (
        <SpaceBetween size="s">
          <Alert type="info" header="Schema changed (compatible)">
            New columns detected. Evolving schema is safe for downstream consumers.
          </Alert>
          <Table
            columnDefinitions={[
              { id: 'col', header: 'New column', cell: (item: { columnName: string; dataType: string }) => item.columnName },
              { id: 'type', header: 'Data type', cell: (item: { columnName: string; dataType: string }) => item.dataType },
            ]}
            items={schemaDiff.added}
            variant="embedded"
          />
          <Box textAlign="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCancel}>Cancel upload</Button>
              <Button variant="primary" onClick={handleEvolveSchema}>
                Evolve schema &amp; continue
              </Button>
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      );
    }
    if (schemaCheck === 'breaking' && schemaDiff) {
      const breakingItems = [
        ...(schemaDiff.removed || []).map(c => ({ columnName: c.columnName, change: `Removed (was ${c.dataType})` })),
        ...(schemaDiff.typeChanges || []).map(c => ({ columnName: c.columnName, change: `${c.oldType} -> ${c.newType}` })),
      ];
      return (
        <SpaceBetween size="s">
          <Alert type="error" header="Breaking schema change detected">
            Columns were removed or types changed. This upload cannot proceed with the current dataset.
          </Alert>
          <Table
            columnDefinitions={[
              { id: 'col', header: 'Column', cell: (item: { columnName: string; change: string }) => item.columnName },
              { id: 'change', header: 'Change', cell: (item: { columnName: string; change: string }) => item.change },
            ]}
            items={breakingItems}
            variant="embedded"
          />
          <Box textAlign="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCancel}>Cancel upload</Button>
              <Button variant="primary" onClick={() => { handleCancel(); navigate('/starnexus/uploader/datasets/create'); }}>
                Create new dataset
              </Button>
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      );
    }
    return null;
  })();

  // Only show "Continue to execute" when schema check passed (identical or evolved)
  const canProceed = fileId && !batchId && (schemaCheck === 'identical' || schemaCheck === null);

  const step1 = (
    <SpaceBetween size="m">
      <Alert type="info">
        Uploading a new snapshot to <Box variant="strong" display="inline">{dataset.datasetName}</Box>.
        Schema, SQL transform, and output configuration are inherited from the dataset.
      </Alert>
      <UploadSampleStep
        file={uploadedFile}
        preview={filePreview}
        error={fileError}
        onFileChange={handleFileChange}
      />
      {registerError && (
        <Alert type="error" dismissible onDismiss={() => setRegisterError(null)}>{registerError}</Alert>
      )}
      {evolvedVersion && (
        <Alert type="success">Schema evolved to v{evolvedVersion}</Alert>
      )}
      {schemaDiffUI}
      {canProceed && (
        <Box textAlign="right">
          <Button variant="primary" loading={registering} onClick={handleGoToExecute}>
            Continue to execute
          </Button>
        </Box>
      )}
    </SpaceBetween>
  );

  const step2 = (
    <SpaceBetween size="m">
      <Alert type="info">
        Append mode. Only Glue Job runs. Andes table + DataCraft sync are skipped
        (they already exist from the first create).
      </Alert>
      {evolvedVersion && (
        <Alert type="success">Schema evolved to v{evolvedVersion}</Alert>
      )}
      {!batchId ? (
        <Box textAlign="center" padding="l"><Spinner /></Box>
      ) : (
        <ExecuteStep
          config={config}
          datasetId={dataset.datasetId}
          fileId={fileId}
          mode="append"
          batchId={batchId}
          onComplete={(passed) => { if (passed) setExecCompleted(true); }}
        />
      )}
    </SpaceBetween>
  );

  return (
    <Modal
      visible={visible}
      onDismiss={handleCancel}
      header={`Upload new snapshot to ${dataset.datasetName}`}
      size="max"
    >
      <Wizard
        i18nStrings={{
          stepNumberLabel: (n) => `Step ${n}`,
          collapsedStepsLabel: (n, total) => `Step ${n} of ${total}`,
          skipToButtonLabel: (step) => `Skip to ${step.title}`,
          navigationAriaLabel: 'Steps',
          cancelButton: execCompleted ? 'Close' : 'Cancel',
          previousButton: 'Previous',
          nextButton: 'Next',
          submitButton: 'Done',
          optional: 'optional',
        }}
        activeStepIndex={activeStepIndex}
        steps={[
          { title: 'Upload new file', description: 'Upload the next batch', content: step1, isOptional: false },
          { title: 'Execute append', description: 'Run Glue Job in append mode', content: step2, isOptional: false },
        ]}
        onCancel={handleCancel}
        onSubmit={() => { reset(); onDismiss(); }}
        onNavigate={({ detail }) => {
          if (detail.requestedStepIndex === 1 && !batchId) return;
          setActiveStepIndex(detail.requestedStepIndex);
        }}
        isLoadingNextStep={registering}
      />
    </Modal>
  );
};

export default QuickUploadModal;
